import { simulateImpulse } from "../dynamics/simulation";
import type { ApplyImpulseAction } from "../types/actions";
import type { SimulationEntityState } from "../types/dynamics";
import type { KineticCoreConfig } from "../types/game-config";
import type { KineticCoreState } from "../types/game-state";
import { getEntityById } from "../utils/entity-helpers";
import { getOpponentId } from "../utils/participant-helpers";
import { evaluateOutcome } from "./evaluate-outcome.applier";

export function applyImpulse(
  state: KineticCoreState,
  action: ApplyImpulseAction,
  config: KineticCoreConfig,
): KineticCoreState {
  const impulseEntity = getEntityById(state.entities, action.entityId);
  if (!impulseEntity) {
    throw new Error(`Entity not found: ${action.entityId}`);
  }

  const simulation = simulateImpulse(
    state.entities,
    action.entityId,
    action.impulse,
    config,
  );

  const finalStatesById = new Map<string, SimulationEntityState>();
  for (const item of simulation.finalEntityStates) {
    finalStatesById.set(item.id, item);
  }

  const updatedEntities = state.entities.map((entity) => {
    const finalState = finalStatesById.get(entity.id);
    if (!finalState) return entity;

    return {
      ...entity,
      x: finalState.x,
      y: finalState.y,
      eliminated: finalState.eliminated,
    };
  });

  const afterImpulse: KineticCoreState = {
    ...state,
    entities: updatedEntities,
    lastRecording: simulation.recording,
  };

  const afterOutcome = evaluateOutcome(
    afterImpulse,
    action.participantId,
    config,
  );
  if (afterOutcome.phase === "ENDED") {
    return afterOutcome;
  }

  return {
    ...afterOutcome,
    turn: state.turn + 1,
    currentParticipant: getOpponentId(
      state.participants,
      state.currentParticipant,
    ),
  };
}
